import { QueryClient } from '@tanstack/react-query'
import { postOrderCompositionBulk, postOrderDataBulk, ORDERS_SUMMARY_KEY, ORDERS_TOTALS_KEY } from '@/feature/financial-model/ecommerce/revenues/api'
import { GETOrderSummaryResponse, OrderDataPostRequest } from '@/feature/financial-model/ecommerce/revenues/api/types'
import { TOTALS_KEY, resetGlobalQueryKey } from '@/api/query-keys'
import { parsePeriod } from '@/utils/sidepanel'

const isWithinRuleRange = (period: string, startFrom?: { year: number; month: number }, exactPeriod?: { year: number; month: number }) => {
  const { year: pYear, month: pMonth } = parsePeriod(period)

  return (
    (!startFrom && !exactPeriod) ||
    (startFrom && (pYear! > startFrom.year || (pYear === startFrom.year && pMonth! >= startFrom.month))) ||
    (exactPeriod && pYear === exactPeriod.year && pMonth === exactPeriod.month)
  )
}

const getPeriodMapKey = (year: number, month: number) => `${year}-${String(month).padStart(2, `0`)}`

const updateCacheGeneric = (
  queryClient: QueryClient,
  financialModelId: string,
  customerSegmentId: string,
  updatePeriodData: (periodData: any) => any
) => {
  const queryKey = ORDERS_SUMMARY_KEY.concat([financialModelId])

  queryClient.setQueryData<GETOrderSummaryResponse>(queryKey, current => {
    if (!current) return current

    return {
      ...current,
      orders_summary: current.orders_summary.map(summary => {
        if (summary.customer_segment_id !== customerSegmentId) return summary

        return {
          ...summary,
          data: summary.data.map(periodData => {
            if (periodData.period_type !== 'forecast') return periodData
            return updatePeriodData(periodData)
          })
        }
      })
    }
  })
}

export const updateCacheForOrderCompositionValue = (
  queryClient: QueryClient,
  financialModelId: string,
  customerSegmentId: string,
  productId: string,
  value: number,
  startFrom?: { year: number; month: number },
  exactPeriod?: { year: number; month: number }
) => {
  updateCacheGeneric(queryClient, financialModelId, customerSegmentId, periodData => {
    if (!isWithinRuleRange(periodData.period, startFrom, exactPeriod)) return periodData

    return {
      ...periodData,
      order_compositions: periodData.order_compositions.map((comp: any) => {
        if (comp.product_id !== productId) return comp
        return { ...comp, value: String(value) }
      })
    }
  })
}

export const updateCacheForOrderCompositionPeriodValues = (
  queryClient: QueryClient,
  financialModelId: string,
  customerSegmentId: string,
  productId: string,
  periodValuesByKey: Record<string, number>
) => {
  updateCacheGeneric(queryClient, financialModelId, customerSegmentId, periodData => {
    const { year: pYear, month: pMonth } = parsePeriod(periodData.period)

    if (pYear === undefined || pMonth === undefined) return periodData

    const key = getPeriodMapKey(pYear, pMonth)
    const mappedValue = periodValuesByKey[key]

    if (mappedValue === undefined) return periodData

    return {
      ...periodData,
      order_compositions: periodData.order_compositions.map((comp: any) => {
        if (comp.product_id !== productId) return comp
        return { ...comp, value: String(mappedValue) }
      })
    }
  })
}

export const updateCacheForOrderDataValue = (
  queryClient: QueryClient,
  financialModelId: string,
  customerSegmentId: string,
  mutateDataKey: string,
  value: number,
  startFrom?: { year: number; month: number },
  exactPeriod?: { year: number; month: number }
) => {
  updateCacheGeneric(queryClient, financialModelId, customerSegmentId, periodData => {
    if (!isWithinRuleRange(periodData.period, startFrom, exactPeriod)) return periodData

    return {
      ...periodData,
      order_data: {
        ...periodData.order_data,
        [mutateDataKey]: value
      }
    }
  })
}

export const updateCacheForOrderDataPeriodValues = (
  queryClient: QueryClient,
  financialModelId: string,
  customerSegmentId: string,
  mutateDataKey: string,
  periodValuesByKey: Record<string, number>
) => {
  updateCacheGeneric(queryClient, financialModelId, customerSegmentId, periodData => {
    const { year: pYear, month: pMonth } = parsePeriod(periodData.period)

    if (pYear === undefined || pMonth === undefined) return periodData

    const key = getPeriodMapKey(pYear, pMonth)
    const mappedValue = periodValuesByKey[key]

    if (mappedValue === undefined) return periodData

    return {
      ...periodData,
      order_data: {
        ...periodData.order_data,
        [mutateDataKey]: mappedValue
      }
    }
  })
}

const applyValuesGeneric = async ({
  financialModelId,
  rows,
  payloadMapper,
  updateCache,
  postBulk,
  queryClient
}: {
  financialModelId: string
  rows: any[]
  payloadMapper: (row: any) => any
  updateCache: () => void
  postBulk: (fmId: string, payload: any[]) => Promise<any>
  queryClient: QueryClient
}) => {
  const forecastRows = rows.filter((row: any) => row.period_type === 'forecast')
  if (!forecastRows.length) return

  const bulkPayload = forecastRows.map(payloadMapper)

  updateCache()

  await postBulk(financialModelId, bulkPayload)

  queryClient.invalidateQueries({
    queryKey: ORDERS_SUMMARY_KEY.concat([financialModelId])
  })

  queryClient.invalidateQueries({
    queryKey: ORDERS_TOTALS_KEY.concat([financialModelId])
  })

  resetGlobalQueryKey([TOTALS_KEY])
}

export const applyOrderCompositionValues = async ({
  financialModelId,
  customerSegmentId,
  orderProduct,
  value,
  periodValuesByKey,
  startFrom,
  exactPeriod,
  queryClient
}: {
  financialModelId: string
  customerSegmentId: string
  orderProduct: any
  value?: number
  periodValuesByKey?: Record<string, number>
  startFrom?: { year: number; month: number }
  exactPeriod?: { year: number; month: number }
  queryClient: QueryClient
}) => {
  const hasPeriodValueMap = Boolean(periodValuesByKey && Object.keys(periodValuesByKey).length)

  await applyValuesGeneric({
    financialModelId,
    rows: orderProduct.orders,
    queryClient,
    postBulk: postOrderCompositionBulk,
    updateCache: () => {
      if (hasPeriodValueMap && periodValuesByKey) {
        updateCacheForOrderCompositionPeriodValues(queryClient, financialModelId, customerSegmentId, orderProduct.productId, periodValuesByKey)
        return
      }

      updateCacheForOrderCompositionValue(
        queryClient,
        financialModelId,
        customerSegmentId,
        orderProduct.productId,
        value ?? 0,
        startFrom,
        exactPeriod
      )
    },
    payloadMapper: (row: any) => {
      const { year: pYear, month: pMonth } = parsePeriod(row.period)
      const periodMapKey = getPeriodMapKey(pYear ?? 0, pMonth ?? 0)
      const mappedValue = periodValuesByKey?.[periodMapKey]

      let valueToApply = Number(row.value ?? 0)

      if (hasPeriodValueMap) {
        valueToApply = mappedValue ?? Number(row.value ?? 0)
      } else if (isWithinRuleRange(row.period, startFrom, exactPeriod)) {
        valueToApply = value ?? valueToApply
      }

      return {
        product_id: orderProduct.productId,
        customer_segment_id: customerSegmentId,
        month: pMonth ?? 0,
        year: pYear ?? 0,
        value: valueToApply,
        config: row.config
      }
    }
  })
}

export const applyOrderCompositionValueToRow = async (
  value: number,
  startFrom: { year: number; month: number } | undefined,
  exactPeriod: { year: number; month: number } | undefined,
  financialModelId: string | undefined,
  payload: any,
  queryClient: QueryClient
) => {
  if (!financialModelId || !payload?.orderProduct || !payload?.customerSegmentId) return

  await applyOrderCompositionValues({
    financialModelId,
    customerSegmentId: payload.customerSegmentId,
    orderProduct: payload.orderProduct,
    value,
    startFrom,
    exactPeriod,
    queryClient
  })
}

export const applyOrderDataValues = async ({
  financialModelId,
  customerSegmentId,
  orderDataPeriods,
  mutateDataKey,
  value,
  periodValuesByKey,
  startFrom,
  exactPeriod,
  queryClient
}: {
  financialModelId: string
  customerSegmentId: string
  orderDataPeriods: any[]
  mutateDataKey: string
  value?: number
  periodValuesByKey?: Record<string, number>
  startFrom?: { year: number; month: number }
  exactPeriod?: { year: number; month: number }
  queryClient: QueryClient
}) => {
  const hasPeriodValueMap = Boolean(periodValuesByKey && Object.keys(periodValuesByKey).length)

  await applyValuesGeneric({
    financialModelId,
    rows: orderDataPeriods,
    queryClient,
    postBulk: postOrderDataBulk,
    updateCache: () => {
      if (hasPeriodValueMap && periodValuesByKey) {
        updateCacheForOrderDataPeriodValues(queryClient, financialModelId, customerSegmentId, mutateDataKey, periodValuesByKey)
        return
      }

      updateCacheForOrderDataValue(queryClient, financialModelId, customerSegmentId, mutateDataKey, value ?? 0, startFrom, exactPeriod)
    },
    payloadMapper: (row: any) => {
      const { year: pYear, month: pMonth } = parsePeriod(row.period)
      const withinRange = isWithinRuleRange(row.period, startFrom, exactPeriod)
      const periodMapKey = getPeriodMapKey(pYear ?? 0, pMonth ?? 0)
      const mappedValue = periodValuesByKey?.[periodMapKey]

      const payload: OrderDataPostRequest = {
        customer_segment_id: customerSegmentId,
        month: pMonth ?? 0,
        year: pYear ?? 0,
        aov: Number(row.order_data.aov ?? 0),
        avg_order_discounts: Number(row.order_data.avg_order_discounts ?? 0),
        avg_orders_per_customer: Number(row.order_data.avg_orders_per_customer ?? 0),
        product_returns_percentage: Number(row.order_data.product_returns_percentage ?? 0),
        net_revenues_after_returns: Number(row.revenues.net_revenues_after_returns ?? 0),
        return_amount: Number(row.order_data.return_amount ?? 0),
        order_discount_amount: Number(row.order_data.order_discount_amount ?? 0),
        num_of_orders: Number(row.order_data.num_of_orders ?? 0),
        num_of_customers: Number(row.order_data.num_of_customers ?? 0),
        num_of_items: Number(row.order_data.num_of_items ?? 0),
        config: row.order_data.config
      }

      if (mutateDataKey === `avg_orders_per_customer`) {
        payload.num_of_orders = null
        payload.num_of_customers = null
      }

      if (hasPeriodValueMap) {
        if (mappedValue !== undefined) {
          const target = payload as any
          target[mutateDataKey] = mappedValue
        }
      } else if (withinRange) {
        const target = payload as any
        target[mutateDataKey] = value
      }

      return payload
    }
  })
}

export const applyOrderDataValueToRow = async (
  value: number,
  startFrom: { year: number; month: number } | undefined,
  exactPeriod: { year: number; month: number } | undefined,
  financialModelId: string | undefined,
  payload: any,
  queryClient: QueryClient
) => {
  if (!financialModelId || !payload?.orderDataPeriods || !payload.mutateDataKey || !payload.customerSegmentId) return

  if (payload.mutateDataKey === `value`) return

  await applyOrderDataValues({
    financialModelId,
    customerSegmentId: payload.customerSegmentId,
    orderDataPeriods: payload.orderDataPeriods,
    mutateDataKey: payload.mutateDataKey,
    value,
    startFrom,
    exactPeriod,
    queryClient
  })
}
